import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";
import IntegrationActivityLog from "../IntegrationActivityLog";
import { SectionCard, Divider } from "@/components/ds";
import IconByName from "@/components/ds/IconByName";
import IntegrationStickyHeader, { IntegrationTab } from "../shared/IntegrationStickyHeader";

const NEXTIVA_COLOR = "#7E22CE"; // Nextiva purple

interface NextivaConfig {
  configured: boolean;
  id?: number;
  displayName?: string;
  accountId?: string;
  enableScreenPop?: boolean;
  enableCallLogging?: boolean;
  enableClickToCall?: boolean;
  createdAt?: string;
}

type Tab = "overview" | "config" | "activity";

const SCREEN_POP_INTEGRATIONS = [
  { name: "HubSpot", flag: "hubspot", icon: "Briefcase", note: "Searches contacts by phone. Returns company, deal stage, and last activity." },
  { name: "Salesforce", flag: "salesforce", icon: "Briefcase", note: "Searches Contact and Lead records. Returns account, open cases, and opportunities." },
  { name: "ServiceNow", flag: "servicenow", icon: "Server", note: "Searches sys_user by phone. Returns open incidents and CI assignments." },
  { name: "ServiceTitan", flag: "servicetitan", icon: "Building", note: "Searches customers by phone. Returns open jobs, next appointment, and invoice balance." },
  { name: "VAST Retail", flag: "vast", icon: "Building", note: "Searches customers and vehicles by phone. Returns service history and open work orders." },
  { name: "NetSuite", flag: "netsuite", icon: "DollarSign", note: "Searches CRM contacts by phone. Returns open sales orders and invoice status." },
  { name: "Dynamics 365", flag: "dynamics", icon: "Briefcase", note: "Searches contacts and accounts by phone. Returns open cases and opportunities." },
  { name: "Jobber", flag: "jobber", icon: "Calendar", note: "Searches clients by phone. Returns upcoming visits and unpaid invoices." },
  { name: "Airtable", flag: "airtable", icon: "Database", note: "Searches any table for a matching phone field. Returns full record." },
  { name: "Pipedrive", flag: "pipedrive", icon: "Briefcase", note: "Searches persons by phone. Returns deal pipeline and last contact date." },
  { name: "Zoho CRM", flag: "zoho", icon: "Briefcase", note: "Searches contacts and leads by phone. Returns account and deal stage." },
];

function ScreenPopExplainer() {
  return (
    <SectionCard title="How screen pop works" description="Every phone integration uses the same automatic lookup system — no configuration per integration required.">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
        {[
          {
            step: "1",
            icon: "Phone",
            title: "Call arrives",
            body: "An inbound call rings in your Nextiva, Teams Phone, or SkySwitch system. The caller's phone number is sent to Velaro via webhook in real time."
          },
          {
            step: "2",
            icon: "Search",
            title: "Parallel lookup",
            body: "Velaro runs a simultaneous search across every CRM and service integration configured for your site — HubSpot, Salesforce, ServiceTitan, VAST, and more — all in parallel."
          },
          {
            step: "3",
            icon: "FileText",
            title: "Agent sees context",
            body: "The first match wins. The agent's sidebar shows the customer name, company, account status, and key data fields — before they even pick up the phone."
          }
        ].map((s) => (
          <div key={s.step} className="flex gap-3">
            <span
              className="flex-shrink-0 w-7 h-7 rounded-full text-white text-xs flex items-center justify-center font-bold mt-0.5"
              style={{ backgroundColor: NEXTIVA_COLOR }}
            >
              {s.step}
            </span>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-zinc-600"><IconByName name={s.icon} className="w-4 h-4" /></span>
                <div className="text-sm font-semibold text-zinc-700">{s.title}</div>
              </div>
              <div className="text-xs text-zinc-500 leading-relaxed">{s.body}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-4">
        <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-3">Integrations with screen pop support</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {SCREEN_POP_INTEGRATIONS.map((intg) => (
            <div key={intg.flag} className="flex items-start gap-2.5 py-1">
              <span className="mt-0.5 text-zinc-600" aria-hidden="true"><IconByName name={intg.icon} className="w-4 h-4" /></span>
              <div>
                <span className="text-xs font-medium text-zinc-700">{intg.name}</span>
                <span className="text-xs text-zinc-400 ml-2">{intg.note}</span>
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-zinc-400 mt-3">
          Any integration without a matching record returns no screen pop — the call still connects normally. New integrations with phone lookup are added automatically as you enable them.
        </p>
      </div>
    </SectionCard>
  );
}

function PhoneSystemsOverview() {
  return (
    <SectionCard title="Supported phone systems" description="Velaro works with any SIP/VoIP system via webhook. These three platforms have native integrations.">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          {
            name: "Nextiva",
            logo: "/img/nextiva.svg",
            color: "#7E22CE",
            status: "available",
            tagline: "Enterprise UCaaS + Contact Center",
            features: ["Inbound call screen pop", "Click-to-call from sidebar", "Post-call activity logging", "Webhook-based CTI event stream"],
            setup: "Configure via the Connect tab on this page."
          },
          {
            name: "Microsoft Teams Phone",
            logo: "/img/ms-teams-phone.svg",
            color: "#0078D4",
            status: "contact-sales",
            tagline: "Direct Routing + Calling Plans",
            features: ["Screen pop on Teams inbound calls", "Works with Direct Routing and Calling Plans", "Microsoft Graph Communications API", "Azure AD app registration required"],
            setup: "Requires Azure app registration with Calls.AccessMedia.All scope. Contact your Velaro account manager."
          },
          {
            name: "SkySwitch",
            logo: "/img/skyswitch.svg",
            color: "#1A56DB",
            status: "contact-sales",
            tagline: "UCaaS Reseller Platform",
            features: ["Screen pop on SkySwitch inbound calls", "SIP trunk and hosted PBX compatible", "Reseller / MSP deployment model", "Webhook-based event integration"],
            setup: "Contact your SkySwitch account manager for webhook configuration, then reach out to Velaro support."
          }
        ].map((sys) => (
          <div key={sys.name} className="border border-zinc-200 rounded-xl p-4 flex flex-col">
            <div className="flex items-center gap-3 mb-3">
              <img src={sys.logo} alt={sys.name} className="w-8 h-8 object-contain" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              <div>
                <div className="text-sm font-semibold text-zinc-800">{sys.name}</div>
                <div className="text-xs text-zinc-400">{sys.tagline}</div>
              </div>
            </div>
            <div
              className={`text-xs font-medium px-2 py-0.5 rounded-full self-start mb-3 ${
                sys.status === "available"
                  ? "bg-green-100 text-green-700"
                  : "bg-zinc-100 text-zinc-500"
              }`}
            >
              {sys.status === "available" ? "Available" : "Contact sales"}
            </div>
            <div className="space-y-1.5 flex-1">
              {sys.features.map((f, i) => (
                <div key={i} className="flex items-start gap-2">
                  <div className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: sys.color }} />
                  <span className="text-xs text-zinc-600">{f}</span>
                </div>
              ))}
            </div>
            <Divider />
            <p className="text-xs text-zinc-400">{sys.setup}</p>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

function ClickToCallExplainer() {
  return (
    <SectionCard title="Click-to-call" description="Agents can initiate outbound calls directly from the Velaro sidebar — no manual dialing.">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="space-y-3">
          <div className="text-sm text-zinc-600 leading-relaxed">
            When click-to-call is enabled, every phone number in the agent sidebar becomes a clickable button. Clicking it:
          </div>
          <div className="space-y-2">
            {[
              "Sends a call initiation request to your connected VoIP system",
              "Opens the Nextiva softphone (or rings the desk phone) automatically",
              "Creates a call activity on the customer record in your CRM",
              "Logs the outbound call in the Velaro activity log for reporting",
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-2">
                <div className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 bg-purple-500" />
                <span className="text-sm text-zinc-600">{item}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-zinc-800 rounded-xl p-4 text-xs font-mono text-zinc-300">
          <div className="text-zinc-500 mb-3">// Agent sidebar — customer panel</div>
          <div className="space-y-2">
            <div className="text-zinc-400">Contact: <span className="text-white">Sarah Johnson</span></div>
            <div className="text-zinc-400">Company: <span className="text-white">Acme HVAC</span></div>
            <div className="text-zinc-400">Open jobs: <span className="text-yellow-400">2</span></div>
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-zinc-700">
              <div className="bg-purple-600 text-white text-xs px-3 py-1 rounded-lg cursor-pointer">
                📞 +1 (410) 555-0182
              </div>
              <span className="text-zinc-500 text-xs">← click to call</span>
            </div>
          </div>
        </div>
      </div>
    </SectionCard>
  );
}

// ── Connect form ───────────────────────────────────────────────────────────────

function ConfigTab({ config, onSaved }: { config: NextivaConfig | null; onSaved(): void }) {
  const api = useApi();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [form, setForm] = useState({
    apiKey: "",
    accountId: config?.accountId ?? "",
    webhookSecret: "",
    displayName: config?.displayName ?? "",
    enableScreenPop: config?.enableScreenPop ?? true,
    enableClickToCall: config?.enableClickToCall ?? true,
    enableCallLogging: config?.enableCallLogging ?? false,
  });

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const resp = await api.messaging.post("Nextiva", form);
      if (resp.ok) {
        onSaved();
      } else {
        setSaveError("Failed to save. Please check your settings.");
      }
    } catch {
      setSaveError("Network error. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard
      title={config?.configured ? "Nextiva Settings" : "Connect Nextiva"}
      action={
        <button
          type="button"
          onClick={save}
          disabled={saving || !form.apiKey}
          className="text-sm px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50 font-medium"
          style={{ backgroundColor: NEXTIVA_COLOR }}
        >
          {saving ? "Saving…" : config?.configured ? "Update" : "Save"}
        </button>
      }
    >
      <div className="max-w-lg space-y-4">
        <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
          <strong>Note:</strong> Nextiva exposes call events via their JavaScript SDK and webhook API. Contact your Nextiva account manager for webhook documentation and your API key.
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-700 mb-1">Display Name</label>
          <input
            className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm"
            value={form.displayName}
            onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
            placeholder="e.g. Main Office"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-700 mb-1">API Key <span className="text-red-500">*</span></label>
          <input
            type="password"
            className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm"
            value={form.apiKey}
            onChange={(e) => setForm((f) => ({ ...f, apiKey: e.target.value }))}
            placeholder="From Nextiva developer portal"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-700 mb-1">Account ID</label>
          <input
            className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm"
            value={form.accountId}
            onChange={(e) => setForm((f) => ({ ...f, accountId: e.target.value }))}
            placeholder="Nextiva account or tenant ID"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-700 mb-1">Webhook Secret <span className="text-zinc-400 font-normal">(optional)</span></label>
          <input
            type="password"
            className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm"
            value={form.webhookSecret}
            onChange={(e) => setForm((f) => ({ ...f, webhookSecret: e.target.value }))}
            placeholder="For HMAC signature validation"
          />
        </div>

        <Divider />
        <div>
          <div className="text-xs font-medium text-zinc-700 mb-3">Feature toggles</div>
          <div className="space-y-2">
            {[
              { key: "enableScreenPop", label: "Screen pop on inbound calls", desc: "Show customer info in the agent sidebar when a call arrives" },
              { key: "enableClickToCall", label: "Click-to-call", desc: "Let agents initiate calls by clicking a phone number in the sidebar" },
              { key: "enableCallLogging", label: "Post-call activity logging", desc: "Create a call activity on the CRM record when the call ends (requires CRM integration)" },
            ].map((opt) => (
              <label key={opt.key} className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={(form as any)[opt.key]}
                  onChange={(e) => setForm((f) => ({ ...f, [opt.key]: e.target.checked }))}
                  className="mt-0.5 rounded border-zinc-300"
                />
                <div>
                  <div className="text-sm text-zinc-700 group-hover:text-zinc-900">{opt.label}</div>
                  <div className="text-xs text-zinc-400">{opt.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {saveError && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-lg">{saveError}</div>
        )}
      </div>
    </SectionCard>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function Nextiva() {
  const api = useApi();
  const [tab, setTab] = useState<Tab>("overview");
  const [config, setConfig] = useState<NextivaConfig | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.messaging.get("Nextiva");
      if (resp.ok) setConfig(await resp.json());
    } catch {
      setConfig(null);
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <LoadingSpinner />;

  const tabsJSX = (
    <>
      <IntegrationTab active={tab === "overview"} onClick={() => setTab("overview")}>Overview</IntegrationTab>
      <IntegrationTab active={tab === "config"} onClick={() => setTab("config")}>{config?.configured ? "Settings" : "Connect"}</IntegrationTab>
      <IntegrationTab active={tab === "activity"} onClick={() => setTab("activity")}>Activity Log</IntegrationTab>
    </>
  );

  return (
    <IntegrationStickyHeader
      title="Nextiva"
      tabs={tabsJSX}
      headerActions={
        config?.configured
          ? <span className="text-xs bg-green-100 text-green-700 font-medium px-2.5 py-1 rounded-full">Connected</span>
          : undefined
      }
    >
      {tab === "overview" && (
        <div className="space-y-4">
          <SectionCard title="VoIP Phone Integration" action={
            !config?.configured
              ? <button type="button" onClick={() => setTab("config")} className="text-sm px-4 py-2 text-white rounded-lg hover:opacity-90 font-medium" style={{ backgroundColor: NEXTIVA_COLOR }}>Connect</button>
              : <span className="text-xs bg-green-100 text-green-700 font-medium px-2.5 py-1 rounded-full">Connected</span>
          }>
            <div className="text-zinc-500 text-sm leading-relaxed mb-4">
              Connect your phone system to Velaro to get instant customer context on every inbound call. When your phone rings, Velaro automatically looks up the caller across all your integrations — CRM, service management, e-commerce — and shows the result in the agent sidebar before they answer.
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="text-xs font-semibold px-2 py-1 rounded-full bg-purple-100 text-purple-800">Screen pop</span>
              <span className="text-xs font-semibold px-2 py-1 rounded-full bg-zinc-100 text-zinc-600">Click-to-call</span>
              <span className="text-xs font-semibold px-2 py-1 rounded-full bg-zinc-100 text-zinc-600">Call logging</span>
              <span className="text-xs font-semibold px-2 py-1 rounded-full bg-zinc-100 text-zinc-600">{SCREEN_POP_INTEGRATIONS.length} CRM sources</span>
            </div>
          </SectionCard>

          <ScreenPopExplainer />
          <PhoneSystemsOverview />
          <ClickToCallExplainer />
        </div>
      )}

      {tab === "config" && (
        <ConfigTab config={config} onSaved={async () => { await load(); setTab("overview"); }} />
      )}

      {tab === "activity" && <IntegrationActivityLog integrationSlug="Nextiva" />}
    </IntegrationStickyHeader>
  );
}

export function BackToIntegrationLandingNextiva() {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate("/integrations")}
      className="text-sm text-zinc-500 hover:text-zinc-700 flex items-center gap-1"
    >
      ← Integrations
    </button>
  );
}
